"use client";

import type { KeyboardEvent } from "react";
import { Camera, Mail, MessageCircle, Phone, ShieldAlert, ShieldCheck, ShieldOff } from "lucide-react";
import { cn } from "@/lib/cn";
import type { PrimitiveState } from "@/lib/types";
import {
  EmptyState,
  FailedActionState,
  PartialStaleBanner,
  PermissionRestrictedState,
  ProviderDisconnectedState,
  RetryingState,
  SkeletonLine,
} from "./state-parts";
import { Pill } from "./pill";

export type GuestChannel = "whatsapp" | "phone" | "email" | "instagram";
export type GuestValueTier = "standard" | "gold" | "vip";
export type GuestConsentState = "granted" | "pending" | "withdrawn";

export interface GuestIdentity {
  name: string;
  initials: string;
  language: string;
  channel: GuestChannel;
  valueTier: GuestValueTier;
  consent: GuestConsentState;
  /** Human-readable relative date, e.g. "18 Aug" or "35 days ago". */
  lastVisit: string;
}

const CHANNEL_ICON: Record<GuestChannel, typeof Phone> = {
  whatsapp: MessageCircle,
  phone: Phone,
  email: Mail,
  instagram: Camera,
};

const CHANNEL_LABEL: Record<GuestChannel, string> = {
  whatsapp: "WhatsApp",
  phone: "Phone",
  email: "Email",
  instagram: "Instagram",
};

const TIER_TONE = {
  standard: "neutral",
  gold: "warning",
  vip: "info",
} as const;

const TIER_LABEL: Record<GuestValueTier, string> = {
  standard: "Standard",
  gold: "Gold",
  vip: "VIP",
};

const CONSENT_META: Record<
  GuestConsentState,
  { icon: typeof ShieldCheck; label: string; tone: "success" | "warning" | "danger" }
> = {
  granted: { icon: ShieldCheck, label: "Marketing consent granted", tone: "success" },
  pending: { icon: ShieldAlert, label: "Marketing consent pending", tone: "warning" },
  withdrawn: { icon: ShieldOff, label: "Marketing consent withdrawn", tone: "danger" },
};

function Avatar({ initials, className }: { initials: string; className?: string }) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-control bg-gradient-to-br from-accent-premium to-brand-primary font-extrabold text-brand-on",
        className,
      )}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

export interface GuestIdentityHeaderProps {
  guest?: GuestIdentity;
  state?: PrimitiveState;
  asOfLabel?: string;
  providerName?: string;
  onReconnect?: () => void;
  onRetry?: () => void;
  onOpenProfile?: () => void;
  className?: string;
}

/**
 * Guest Identity Header — full workspace header variant. Verbatim required
 * fields (docs/COMPONENT_LIBRARY.md): name, language/channel preference,
 * value tier, consent state, last visit.
 */
export function GuestIdentityHeader({
  guest,
  state = "populated",
  asOfLabel,
  providerName = "the guest data provider",
  onReconnect,
  onRetry,
  onOpenProfile,
  className,
}: GuestIdentityHeaderProps) {
  if (state === "loading") {
    return (
      <div
        className={cn("flex items-center gap-4 rounded-card border border-border-subtle bg-surface-primary p-4", className)}
        data-state={state}
      >
        <SkeletonLine className="h-14 w-14 rounded-control" />
        <div className="flex-1 space-y-2">
          <SkeletonLine className="h-4 w-40" />
          <SkeletonLine className="h-3 w-56" />
        </div>
      </div>
    );
  }

  if (state === "empty") {
    return (
      <div className={cn("rounded-card border border-border-subtle bg-surface-primary p-4", className)} data-state={state}>
        <EmptyState title="No guest selected" description="Choose a guest to see their profile summary here." />
      </div>
    );
  }

  if (state === "permission-restricted") {
    return (
      <div className={cn("rounded-card border border-border-subtle bg-surface-primary p-4", className)} data-state={state}>
        <PermissionRestrictedState description="Guest identity details are hidden for your current role." />
      </div>
    );
  }

  if (state === "provider-disconnected") {
    return (
      <div className={cn("rounded-card border border-border-subtle bg-surface-primary p-4", className)} data-state={state}>
        <ProviderDisconnectedState providerName={providerName} onReconnect={onReconnect} />
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className={cn("rounded-card border border-border-subtle bg-surface-primary p-4", className)} data-state={state}>
        <FailedActionState description="Could not load this guest's identity." onRetry={onRetry} />
      </div>
    );
  }

  if (state === "retrying") {
    return (
      <div className={cn("rounded-card border border-border-subtle bg-surface-primary p-4", className)} data-state={state}>
        <RetryingState description="Loading guest identity" />
      </div>
    );
  }

  if (!guest) return null;

  const ChannelIcon = CHANNEL_ICON[guest.channel];
  const consent = CONSENT_META[guest.consent];
  const ConsentIcon = consent.icon;

  return (
    <div className={cn("rounded-card border border-border-subtle bg-surface-primary p-4", className)} data-state={state}>
      {state === "partial-stale" && <PartialStaleBanner asOf={asOfLabel} />}
      <div
        className={cn(
          "flex items-center gap-4",
          onOpenProfile && "cursor-pointer rounded-control focus-visible:outline-2 focus-visible:outline-brand-primary",
        )}
        role={onOpenProfile ? "button" : undefined}
        tabIndex={onOpenProfile ? 0 : undefined}
        onClick={onOpenProfile}
        onKeyDown={(event: KeyboardEvent) => {
          if (onOpenProfile && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onOpenProfile();
          }
        }}
        aria-label={onOpenProfile ? `Open guest profile for ${guest.name}` : undefined}
      >
        <Avatar initials={guest.initials} className="h-14 w-14 text-lg" />
        <div className="min-w-0 flex-1">
          <p className="font-display truncate text-lg font-medium text-text-primary">{guest.name}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Pill tone={TIER_TONE[guest.valueTier]}>{TIER_LABEL[guest.valueTier]}</Pill>
            <span className="inline-flex items-center gap-1 text-xs text-text-secondary">
              <ChannelIcon aria-hidden="true" size={13} />
              {CHANNEL_LABEL[guest.channel]}
            </span>
            <span className="text-xs text-text-secondary">· {guest.language.toUpperCase()}</span>
            <span
              className={cn(
                "inline-flex items-center gap-1 text-xs",
                consent.tone === "success"
                  ? "text-status-success"
                  : consent.tone === "warning"
                    ? "text-status-warning"
                    : "text-status-danger",
              )}
              title={consent.label}
            >
              <ConsentIcon aria-hidden="true" size={13} />
              <span className="sr-only">{consent.label}</span>
            </span>
            <span className="text-xs text-text-secondary">· Last visit {guest.lastVisit}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export interface GuestIdentityChipProps {
  guest: GuestIdentity;
  onOpenProfile?: () => void;
  className?: string;
}

/**
 * Compact Guest Identity chip for use inline inside reservation rows, floor
 * table nodes, inbox threads, etc. Always clickable to the unified guest
 * profile per the cross-cutting screen rule: "every guest-facing object can
 * open the unified guest profile."
 */
export function GuestIdentityChip({ guest, onOpenProfile, className }: GuestIdentityChipProps) {
  return (
    <button
      type="button"
      onClick={onOpenProfile}
      className={cn(
        "inline-flex items-center gap-2 rounded-pill border border-border-subtle bg-surface-secondary py-1 ps-1 pe-3 text-start hover:border-border-strong",
        className,
      )}
      aria-label={`Open guest profile for ${guest.name}`}
    >
      <Avatar initials={guest.initials} className="h-6 w-6 text-[10px]" />
      <span className="text-xs font-bold text-text-primary">{guest.name}</span>
      <Pill tone={TIER_TONE[guest.valueTier]} className="px-1.5 py-0.5 text-[10px]">
        {TIER_LABEL[guest.valueTier]}
      </Pill>
    </button>
  );
}
